import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { BASE_URL } from '../constants/config';
import { setAccessToken, clearAccessToken, setOnUnauthorized } from '../services/api';

const AuthContext = createContext(null);

const decodeJWT = (token) => {
  try {
    return JSON.parse(atob(token.split('.')[1]));
  } catch {
    return null;
  }
};

export function AuthProvider({ children }) {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const refreshTimerRef = useRef(null);
  const refreshRef = useRef(null);

  const clearAuth = () => {
    setIsAuthenticated(false);
    clearAccessToken();
    localStorage.removeItem('refreshToken');
    if (refreshTimerRef.current) {
      clearTimeout(refreshTimerRef.current);
      refreshTimerRef.current = null;
    }
  };

  const applyToken = (token, refreshToken = null) => {
    setAccessToken(token);
    setIsAuthenticated(true);
    if (refreshToken) localStorage.setItem('refreshToken', refreshToken);

    // Schedule proactive refresh 1 minute before expiry
    const decoded = decodeJWT(token);
    if (decoded?.exp) {
      const delay = Math.max(decoded.exp * 1000 - Date.now() - 60000, 0);
      if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
      refreshTimerRef.current = setTimeout(() => refreshRef.current?.(), delay);
    }
  };

  const doRefresh = async () => {
    const refreshToken = localStorage.getItem('refreshToken');
    if (!refreshToken) { clearAuth(); return false; }

    try {
      const response = await fetch(`${BASE_URL}/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken }),
      });
      if (!response.ok) throw new Error('refresh failed');
      const data = await response.json();
      applyToken(data.token);
      return true;
    } catch {
      clearAuth();
      return false;
    }
  };

  // Keep refreshRef current so the setTimeout callback never goes stale
  refreshRef.current = doRefresh;

  // Wire the api service to call clearAuth when a refresh fails mid-flight
  useEffect(() => {
    setOnUnauthorized(clearAuth);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Restore session on mount using the stored refresh token
  useEffect(() => {
    const restore = async () => {
      if (localStorage.getItem('refreshToken')) await doRefresh();
      setLoading(false);
    };
    restore();
    return () => {
      if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const login = async (username, password) => {
    const response = await fetch(`${BASE_URL}/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ username, password }),
    });
    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      throw new Error(data.error || 'Credenziali non valide');
    }
    const data = await response.json();
    applyToken(data.token, data.refreshToken);
  };

  const logout = async () => {
    const refreshToken = localStorage.getItem('refreshToken');
    if (refreshToken) {
      fetch(`${BASE_URL}/auth/logout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken }),
      }).catch(() => {});
    }
    clearAuth();
  };

  return (
    <AuthContext.Provider value={{ isAuthenticated, login, logout, loading }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
